import { Calendar as CalendarIcon, List as ListIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { TaskAssigneeFilter, TaskAssigneeOption, TaskPriorityFilter } from '@/lib/task-filters';

export type TaskView = 'calendar' | 'list';

interface TaskFiltersProps {
  view: TaskView;
  onViewChange: (view: TaskView) => void;
  priorityFilter: TaskPriorityFilter;
  onPriorityFilterChange: (filter: TaskPriorityFilter) => void;
  assigneeFilter: TaskAssigneeFilter;
  onAssigneeFilterChange: (filter: TaskAssigneeFilter) => void;
  assigneeOptions: TaskAssigneeOption[];
}

export function TaskFilters({
  view,
  onViewChange,
  priorityFilter,
  onPriorityFilterChange,
  assigneeFilter,
  onAssigneeFilterChange,
  assigneeOptions,
}: TaskFiltersProps) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
      <div className="flex w-full rounded-lg border bg-muted/40 p-1 sm:inline-flex sm:w-auto" role="group" aria-label="Task view">
        <Button
          type="button"
          size="sm"
          variant={view === 'list' ? 'default' : 'ghost'}
          className="h-8 flex-1 gap-1.5 sm:flex-initial"
          onClick={() => onViewChange('list')}
          data-testid="task-view-toggle-list"
          aria-pressed={view === 'list'}
        >
          <ListIcon className="h-4 w-4" />
          List
        </Button>
        <Button
          type="button"
          size="sm"
          variant={view === 'calendar' ? 'default' : 'ghost'}
          className="h-8 flex-1 gap-1.5 sm:flex-initial"
          onClick={() => onViewChange('calendar')}
          data-testid="task-view-toggle-calendar"
          aria-pressed={view === 'calendar'}
        >
          <CalendarIcon className="h-4 w-4" />
          Calendar
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={priorityFilter}
          onValueChange={(value) => onPriorityFilterChange(value as TaskPriorityFilter)}
        >
          <SelectTrigger className="h-8 w-full min-w-[140px] flex-1 sm:w-[150px] sm:flex-initial" data-testid="task-priority-filter">
            <SelectValue placeholder="Priority" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All priorities</SelectItem>
            <SelectItem value="urgent">Urgent</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="low">Low</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={String(assigneeFilter)}
          onValueChange={(value) => {
            if (value === 'all' || value === 'unassigned') {
              onAssigneeFilterChange(value);
            } else {
              onAssigneeFilterChange(Number(value));
            }
          }}
        >
          <SelectTrigger className="h-8 w-full min-w-[140px] flex-1 sm:w-[160px] sm:flex-initial" data-testid="task-assignee-filter">
            <SelectValue placeholder="Assignee" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All assignees</SelectItem>
            <SelectItem value="unassigned">Unassigned</SelectItem>
            {assigneeOptions.map((assignee) => (
              <SelectItem key={assignee.id} value={String(assignee.id)}>
                {assignee.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
